
import React from 'react';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Languages } from 'lucide-react';
import { Translation } from '@/utils/translationUtils';

interface TranslationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isEditMode: boolean;
  currentTranslation: Translation;
  onTranslationChange: (field: keyof Translation, value: string) => void;
  onSave: () => void;
  translateFn: (key: string) => string;
}

const TranslationDialog: React.FC<TranslationDialogProps> = ({
  isOpen,
  onOpenChange,
  isEditMode,
  currentTranslation,
  onTranslationChange,
  onSave,
  translateFn
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Languages className="h-5 w-5" />
            {isEditMode 
              ? translateFn('translations.editTranslation') 
              : translateFn('translations.addTranslation')
            }
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Edit the translation values below"
              : "Add a new translation with values for each language"
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="key">{translateFn('translations.translationKey')}</Label>
            <Input
              id="key"
              placeholder={translateFn('translations.translationKeyPlaceholder')}
              value={currentTranslation.key}
              onChange={(e) => onTranslationChange('key', e.target.value)}
              readOnly={isEditMode}
              className={isEditMode ? "bg-muted" : ""}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="en">{translateFn('translations.english')}</Label>
            <Input
              id="en"
              placeholder={translateFn('translations.englishTranslationPlaceholder')}
              value={currentTranslation.en}
              onChange={(e) => onTranslationChange('en', e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="no">{translateFn('translations.norwegian')}</Label>
            <Input
              id="no"
              placeholder={translateFn('translations.norwegianTranslationPlaceholder')}
              value={currentTranslation.no}
              onChange={(e) => onTranslationChange('no', e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {translateFn('common.cancel')}
          </Button>
          <Button onClick={onSave}>
            {translateFn('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TranslationDialog;
